import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarCheck, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { SERVICES } from "./Services";
import { useLang } from "@/lib/i18n";

const TIME_SLOTS = [
  { value: "Morning (8am – 12pm)", key: "slot_morning" },
  { value: "Afternoon (12pm – 4pm)", key: "slot_afternoon" },
  { value: "Late Afternoon (4pm – 7pm)", key: "slot_evening" },
];

const CITIES = ["Centralia", "Chehalis", "Olympia", "Nearby area"];

export default function AppointmentForm({ prefill }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    address: "",
    city: "Centralia",
    date: "",
    timeSlot: TIME_SLOTS[0].value,
    serviceType: SERVICES[0].title,
    notes: "",
  });
  const [estimate, setEstimate] = useState(null);
  const [sending, setSending] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (prefill) {
      setEstimate(prefill);
      setForm((f) => ({
        ...f,
        serviceType: prefill.serviceLabel,
        notes: prefill.hourly
          ? `Commercial repair inquiry — hourly $75–$150/hr, ~${prefill.yards} sq yd. Property: Commercial.`
          : `Calculator estimate: ${prefill.yards} sq yd of ${prefill.serviceLabel} (~$${prefill.min.toLocaleString()} – $${prefill.max.toLocaleString()}). Property: ${prefill.propertyType === "commercial" ? "Commercial" : "Residential"}.`,
      }));
    }
  }, [prefill]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const today = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error(t("err_name"));
      return;
    }
    if (!form.address.trim()) {
      toast.error(t("err_addr"));
      return;
    }
    if (!form.date) {
      toast.error(t("err_date"));
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/appointments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          address: form.address.trim(),
          city: form.city,
          date: form.date,
          time_slot: form.timeSlot,
          service_type: form.serviceType,
          phone: form.phone.trim() || null,
          email: form.email.trim().toLowerCase() || null,
          notes: form.notes.trim() || null,
          square_yards: estimate?.yards ?? null,
          price_min: estimate?.min ?? null,
          price_max: estimate?.max ?? null,
        }),
      });
      if (!res.ok) throw new Error("failed");
      setSummary({ ...form });
      toast.success(t("appt_sent"));
      setForm({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        address: "",
        city: "Centralia",
        date: "",
        timeSlot: TIME_SLOTS[0].value,
        serviceType: SERVICES[0].title,
        notes: "",
      });
      setEstimate(null);
    } catch {
      toast.error(t("appt_fail"));
    } finally {
      setSending(false);
    }
  };

  const slotLabel = (value) => {
    const slot = TIME_SLOTS.find((s) => s.value === value);
    return slot ? t(slot.key) : value;
  };

  const cityLabel = (value) => (value === "Nearby area" ? t("city_nearby") : value);

  const modalBody = summary
    ? t("modal_body")
        .replace("{address}", summary.address)
        .replace("{city}", cityLabel(summary.city))
        .replace("{date}", summary.date)
        .replace("{slot}", slotLabel(summary.timeSlot))
    : "";

  return (
    <section
      id="book"
      className="py-16 sm:py-24 lg:py-32 bg-[#FAF8F5]"
      data-testid="book-section"
    >
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#F26522] font-semibold mb-4">
            {t("appt_eyebrow")}
          </p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]">
            {t("appt_h")}
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#475569]">
            {t("appt_sub")}
          </p>
          <div className="mt-10 overflow-hidden rounded-3xl border border-[#E2E8F0] shadow-lg rotate-[-1deg]">
            <img
              src="/job-e.webp"
              alt="Grey luxury vinyl plank installed by PERETE in a living room with fireplace"
              className="w-full h-auto"
              data-testid="book-flyer-img"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-7"
        >
          <form
            onSubmit={submit}
            className="rounded-3xl bg-[#0E1A2B] p-6 sm:p-8 shadow-2xl"
            data-testid="appointment-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                  {t("first_name")}
                </label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={set("firstName")}
                  placeholder="John"
                  className="field-input field-input-dark"
                  data-testid="appointment-first-name-input"
                />
              </div>
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                  {t("last_name")}
                </label>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={set("lastName")}
                  placeholder="Smith"
                  className="field-input field-input-dark"
                  data-testid="appointment-last-name-input"
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                  {t("phone_l")}
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="360-555-0100"
                  className="field-input field-input-dark"
                  data-testid="appointment-phone-input"
                />
              </div>
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                  {t("email_l")} <span className="normal-case tracking-normal text-slate-500">· {t("email_hint")}</span>
                </label>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="name@email.com"
                  className="field-input field-input-dark"
                  data-testid="appointment-email-input"
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                  {t("city_l")}
                </label>
                <select
                  value={form.city}
                  onChange={set("city")}
                  className="field-input field-input-dark"
                  data-testid="appointment-city-select"
                >
                  {CITIES.map((c) => (
                    <option key={c} value={c}>
                      {cityLabel(c)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                  {t("address_l")}
                </label>
                <input
                  type="text"
                  value={form.address}
                  onChange={set("address")}
                  placeholder={t("ph_address")}
                  className="field-input field-input-dark"
                  data-testid="appointment-address-input"
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                  {t("date_l")}
                </label>
                <input
                  type="date"
                  min={today}
                  value={form.date}
                  onChange={set("date")}
                  className="field-input field-input-dark"
                  data-testid="appointment-date-picker"
                />
              </div>
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                  {t("time_l")}
                </label>
                <select
                  value={form.timeSlot}
                  onChange={set("timeSlot")}
                  className="field-input field-input-dark"
                  data-testid="appointment-time-select"
                >
                  {TIME_SLOTS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {t(s.key)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                {t("service_l")}
              </label>
              <select
                value={form.serviceType}
                onChange={set("serviceType")}
                className="field-input field-input-dark"
                data-testid="appointment-service-select"
              >
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title}>
                    {lang === "es" ? s.titleEs : s.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4">
              <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                {t("notes_l")}
              </label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={set("notes")}
                placeholder={t("ph_notes")}
                className="field-input field-input-dark resize-none"
                data-testid="appointment-notes-input"
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F26522] hover:bg-[#D85312] text-white font-display font-bold px-7 py-4 text-base transition-[background-color,transform] duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              data-testid="book-appointment-btn"
            >
              <CalendarCheck className="h-5 w-5" />
              {sending ? t("sending") : t("submit_appt")}
            </button>
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              {t("confirm_note")}
            </p>
          </form>
        </motion.div>
      </div>

      <AnimatePresence>
        {summary && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0E1A2B]/80 backdrop-blur-sm px-4"
            data-testid="appointment-success-modal"
          >
            <motion.div
              initial={{ scale: 0.9, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl"
            >
              <button
                onClick={() => setSummary(null)}
                className="absolute top-4 right-4 rounded-full p-2 text-slate-400 hover:text-[#0E1A2B] hover:bg-slate-100 transition-colors duration-200"
                data-testid="appointment-modal-close"
                aria-label={t("modal_close")}
              >
                <X className="h-5 w-5" />
              </button>
              <CheckCircle2 className="h-12 w-12 text-[#F26522]" />
              <h3 className="mt-4 font-display text-2xl font-bold text-[#0E1A2B]">
                {t("modal_title")}, {summary.firstName}!
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-[#475569]">
                {modalBody}
              </p>
              <button
                onClick={() => setSummary(null)}
                className="mt-6 w-full rounded-full bg-[#0E1A2B] hover:bg-[#F26522] text-white font-display font-bold px-6 py-3.5 text-sm transition-[background-color,transform] duration-200 hover:scale-[1.02] active:scale-95"
                data-testid="appointment-modal-ok"
              >
                {t("modal_done")}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
